import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SettingsToggleRow } from '../../../../components/design-system/common/SettingsPanel';
import { config as loadedConfig, useSettingsApi } from '../../../../common/hooks/use-settings-api.hook';

/**
 * An on/off vendor setting that is on unless switched off (absent = on), saved
 * on its own the moment it is flipped: optimistic, put back with an error if
 * the save fails. So the integration settings' autosave around it is not
 * involved.
 */
export const ConfigToggleSetting = ({ field, id, icon, label, description }: {
    field: string;
    id: string;
    icon: ReactNode;
    label: string;
    description: string;
}) => {
    const { t } = useTranslation();
    const settingsAPI = useSettingsApi();
    // The hook hands back a new object every render; the latest one is enough.
    const apiRef = useRef(settingsAPI);
    apiRef.current = settingsAPI;

    const [checked, setChecked] = useState<boolean>(() => (loadedConfig as any)?.[field] !== false);
    const [saving, setSaving] = useState(false);
    const touchedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve()
            .then(() => apiRef.current.findConfig())
            .then((cfg: any) => {
                if (cancelled || touchedRef.current || !cfg || typeof cfg !== 'object') return;
                setChecked(cfg[field] !== false);
            })
            .catch(() => {
                // Keep what is shown; flipping it still saves.
            });
        return () => {
            cancelled = true;
        };
    }, [field]);

    const handleChange = async (next: boolean) => {
        touchedRef.current = true;
        setChecked(next);
        setSaving(true);
        try {
            await apiRef.current.updateConfig({ [field]: next });
            if (loadedConfig) (loadedConfig as any)[field] = next;
        } catch {
            setChecked(!next);
            toast.error(t('PRODUCT_PRICING.SETTINGS.SAVE_FAILED', 'Could not save this setting. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <SettingsToggleRow
            id={id}
            icon={icon}
            label={label}
            description={description}
            checked={checked}
            onCheckedChange={handleChange}
            disabled={saving}
        />
    );
};
